import React, { PureComponent } from 'react'
import ReactDOM from 'react-dom'
import { translate } from 'react-polyglot'
import OutsideClickHandler from 'react-outside-click-handler'

import { Text } from 'components'
import caretPosition from './caret-position'
import SuggestionItem from './suggestion-item'
import getCurrentWord from './current-word'

import {
	UP_ARROW_KEY,
	DOWN_ARROW_KEY,
	ENTER_KEY,
	ESCAPE_KEY,
} from './constants'

class SuggestionPortal extends PureComponent {
	constructor(props) {
		super()

		this.state = {
			selectedIndex: 0,
			isOpen: false,
			justOpened: false,
			filteredSuggestions:
				typeof props.suggestions === 'function'
					? []
					: props.suggestions,
		}

		this.portalContainer = React.createRef()

		props.callback.onKeyDown = this.onKeyDown
		props.callback.onKeyUp = this.onKeyUp
		props.callback.onEnter = props.onEnter

		if (typeof props.suggestions === 'function') {
			props.callback.suggestion = undefined
		} else {
			props.callback.suggestion = this.state.filteredSuggestions[
				this.selectedIndex
			]
		}
	}

	componentDidMount = () => {
		const pageContent = document.querySelector('.o-page__content')

		window.addEventListener('resize', this.updatePosition)
		pageContent.addEventListener('scroll', this.updatePosition)
	}

	componentDidUpdate = () => {
		this.updatePosition()
	}

	componentWillUnmount() {
		const pageContent = document.querySelector('.o-page__content')

		window.removeEventListener('resize', this.updatePosition)
		pageContent.removeEventListener('scroll', this.updatePosition)
	}

	setCallbackSuggestion = () => {
		const { filteredSuggestions, selectedIndex } = this.state
		const { callback } = this.props

		if (filteredSuggestions.length) {
			callback.suggestion = filteredSuggestions[selectedIndex]
		} else {
			callback.suggestion = undefined
		}
	}

	setFilteredSuggestions = (filteredSuggestions, resetIndex = false) => {
		const { selectedIndex } = this.state

		this.setState(
			{
				filteredSuggestions,
				selectedIndex: resetIndex ? 0 : selectedIndex,
			},
			this.setCallbackSuggestion
		)
	}

	getFilteredSuggestions = () => {
		const { suggestions, value, capture, t } = this.props

		if (!value.selection.anchor.key) return []

		const { anchorText, selection } = value
		const { offset } = selection.anchor

		const currentWord = getCurrentWord(
			anchorText.text,
			offset - 1,
			offset - 1
		)

		let matchText = this.getMatchText(currentWord, capture)
		matchText = matchText.slice(1, matchText.length)

		if (typeof suggestions === 'function') {
			return suggestions(matchText)
		}

		if (matchText) {
			return suggestions.filter(
				suggestion =>
					suggestion.keys.toLowerCase().indexOf(matchText) !== -1
			)
		}

		return suggestions
	}

	onKeyUp = (e, editor, next) => {
		const match = this.matchCapture()

		if (match) {
			if (
				e.keyCode !== DOWN_ARROW_KEY &&
				e.keyCode !== UP_ARROW_KEY &&
				e.keyCode !== ENTER_KEY &&
				e.keyCode !== ESCAPE_KEY
			) {
				const newFilteredSuggestions = this.getFilteredSuggestions(
					e.key
				)

				if (typeof newFilteredSuggestions.then === 'function') {
					newFilteredSuggestions
						.then(nfs => {
							this.setFilteredSuggestions(nfs, true)
						})
						.catch(() => {
							this.setFilteredSuggestions([], true)
						})
				} else {
					this.setFilteredSuggestions(newFilteredSuggestions, true)
				}
			}
		}

		next()
	}

	onKeyDown = (e, editor, next) => {
		const match = this.matchCapture()
		const { selectedIndex } = this.state

		if (match) {
			const { filteredSuggestions } = this.state

			if (e.keyCode === ESCAPE_KEY) {
				e.preventDefault()

				this.closePortal()

				return false
			}

			if (filteredSuggestions.length > 0) {
				if (e.keyCode === DOWN_ARROW_KEY) {
					e.preventDefault()

					this.setState(
						{
							selectedIndex:
								selectedIndex + 1 === filteredSuggestions.length
									? 0
									: (selectedIndex || 0) + 1,
						},
						() => {
							this.setCallbackSuggestion()
							this.scrollToItem('down')

							return false
						}
					)
				}

				if (e.keyCode === UP_ARROW_KEY) {
					e.preventDefault()

					this.setState(
						{
							selectedIndex:
								selectedIndex === 0
									? filteredSuggestions.length - 1
									: (selectedIndex ||
											filteredSuggestions.length) - 1,
						},
						() => {
							this.setCallbackSuggestion()
							this.scrollToItem('up')

							return false
						}
					)
				}

				if (e.keyCode === ENTER_KEY) {
					e.preventDefault()

					this.setSelectedIndex(0)
					this.props.callback.onEnter(
						this.props.callback.suggestion,
						this.props.callback.editor
					)

					return false
				}
			}
		}

		next()
	}

	getMatchText = (text, trigger) => {
		const matchArr = text.match(trigger)

		if (matchArr) return matchArr[0].toLowerCase()
		return undefined
	}

	matchCapture = () => {
		const {
			value: {
				focusBlock,
				anchorText,
				selection: {
					anchor: { offset, key },
				},
			},
			capture,
			startOfParagraph,
			trigger,
		} = this.props

		if (!key) return ''

		// @TODO: Needed?
		if (
			startOfParagraph &&
			focusBlock.text[0] &&
			focusBlock.text[0] !== trigger
		) {
			this.closePortal()

			return ''
		}

		const currentWord = getCurrentWord(
			anchorText.text,
			offset - 1,
			offset - 1
		)
		const matchText = this.getMatchText(currentWord, capture)

		return matchText
	}

	updatePosition = () => {
		// if (!this.portalContainer.current) return

		const {
			value,
			value: {
				selection: { isBlurred },
			},
			trigger,
			suggestions,
			editor,
		} = this.props

		const { filteredSuggestions, isOpen } = this.state
		const match = this.matchCapture()

		if (value.focusBlock && isOpen) {
			const windowHeight = document.body.clientHeight
			const portal = this.portalContainer.current
			const portalHeight = portal.getBoundingClientRect().height

			const block = editor.findDOMNode(
				editor.value.document.getPath(value.focusBlock.key)
			)
			const blockRect = block.getBoundingClientRect()

			let leftPosition = blockRect.left

			if (!block.classList.contains('o-editor__section')) {
				const parentRect = block.offsetParent.getBoundingClientRect()

				leftPosition = parentRect.left
			}

			const topCorrection = 10
			const tooHigh =
				blockRect.bottom + portalHeight + topCorrection > windowHeight
			const topPosition = tooHigh
				? blockRect.top - portalHeight - topCorrection
				: blockRect.bottom + topCorrection

			if (caretPosition()) {
				portal.style.top = `${topPosition}px`
				portal.style.left = `${leftPosition}px`
			}
		}

		// If text is "/" and suggestion list is filtered, reset
		if (
			value.focusBlock &&
			value.focusBlock.text === trigger &&
			value.focusBlock.type === 'paragraph' &&
			value.document.getParent(value.focusBlock.key).object ===
				'document' &&
			!isBlurred
		) {
			if (filteredSuggestions.length !== suggestions.length) {
				this.setState({ filteredSuggestions: suggestions })
			}

			if (!isOpen) {
				this.showPortal()
			}
		} else if (match === undefined) {
			this.closePortal()
		}
		// else if (isBlurred) {
		// 	if (
		// 		this.props.callback.suggestion
		// 			&& (
		// 				this.props.callback.suggestion.key === 'YouTube'
		// 				|| this.props.callback.suggestion.key === 'Vimeo'
		// 				|| this.props.callback.suggestion.key === 'Draw'
		// 				|| this.props.callback.suggestion.key === 'Figma'
		// 				|| this.props.callback.suggestion.key === 'Gist'
		// 				|| this.props.callback.suggestion.key === 'Codepen'
		// 			)
		// 	) {
		// 		this.closePortal()
		// 	}
		// }
	}

	showPortal = () => {
		const { justOpened } = this.state

		this.setState({ isOpen: true })

		if (!justOpened) {
			setTimeout(() => {
				this.setState({ justOpened: false })
			}, 100)
		}
	}

	closePortal = () => {
		const { callback } = this.props
		const { isOpen } = this.state

		if (!isOpen) return

		this.setState({
			selectedIndex: 0,
			isOpen: false,
			justOpened: false,
		})

		callback.suggestion = undefined
	}

	setSelectedIndex = selectedIndex => {
		this.setState({ selectedIndex })
	}

	scrollToItem = direction => {
		const { selectedIndex } = this.state
		const container = this.portalContainer.current
		const containerRect = container.getBoundingClientRect()
		const item = container.children[0].children[selectedIndex]
		const itemRect = item.getBoundingClientRect()
		const isVisible =
			itemRect.top >= containerRect.top &&
			itemRect.bottom <= containerRect.bottom
		const offset = 10

		if (direction === 'up' && !isVisible) {
			container.scrollTop = item.offsetTop - offset
		} else if (direction === 'down' && !isVisible) {
			container.scrollTop =
				itemRect.height + item.offsetTop - containerRect.height + offset
		}
	}

	handleClickOutside() {
		const { isOpen, justOpened } = this.state

		if (isOpen) {
			if (!justOpened) {
				this.closePortal()
			}
		}
	}

	render = () => {
		const { t } = this.props
		const { filteredSuggestions, selectedIndex, isOpen } = this.state

		if (!isOpen) return null

		let lastGroup = null

		return ReactDOM.createPortal(
			<OutsideClickHandler
				onOutsideClick={() => {
					this.closePortal()
				}}
			>
				<div
					className="c-suggestions is-visible"
					ref={this.portalContainer}
				>
					<div className="c-suggestions__list">
						{filteredSuggestions.length ? (
							filteredSuggestions.map((suggestion, index) => {
								const startOfGroup =
									lastGroup !== suggestion.group
								lastGroup = suggestion.group

								return (
									<div key={suggestion.key}>
										{startOfGroup ? (
											<div className="c-suggestions__group">
												{t(
													`suggestion_groups.${suggestion.group}`
												)}
											</div>
										) : null}
										<SuggestionItem
											index={index}
											className="c-suggestions__item"
											suggestion={suggestion}
											selectedIndex={selectedIndex}
											setSelectedIndex={
												this.setSelectedIndex
											}
											execute={suggestion => {
												// this.setSelectedIndex(0)
												this.props.callback.onEnter(
													suggestion,
													this.props.callback.editor
												)
											}}
											closePortal={this.closePortal}
											editor={this.props.callback.editor}
										/>
									</div>
								)
							})
						) : (
							<div className="c-suggestions__no-results">
								<Text>{t(`suggestions.no_results`)}</Text>
							</div>
						)}
					</div>
				</div>
			</OutsideClickHandler>,
			document.getElementById('root')
		)
	}
}

export default translate()(SuggestionPortal)
